import React, { useEffect, useState } from "react";
import PageTitle from "components/Typography/PageTitle";
import { Card } from "@windmill/react-ui";
import sidebar from "routes/sidebar";
import RoleServices from "services/RoleServices";
import MenuServices from "services/MenuServices";
import PermissionServices from "services/PermissionServices";
import { notifySuccess } from "utils/toast";
const Permission = () => {
  const [permissions, setPermissions] = useState({});
  const [selectedRole, setSelectedRole] = useState("");
  const [, setError] = useState("");
  const [roles, setRoles] = useState([]);
  const [roleMenuData, setroleMenuData] = useState([]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await RoleServices.getAllRole();
        if (response?.status && Array.isArray(response?.data)) {
          const updatedData = response.data.map(({ name, id_profile }) => ({
            name,
            id_profile,
          }));
          setRoles(updatedData);
        } else {
          setError("Invalid data format from API");
        }
      } catch (error) {
        console.error("API Error:", error);
        setError("Failed to fetch roles.");
      }
    };

    fetchRoles();
  }, []);

  useEffect(() => {
    const fetchRoleMenuData = async () => {
      try {
        const response = await MenuServices.getAllAdminMenu();

        if (response?.status) {
          const updatedData = response.data.map((menu) => ({
            id: menu.id_tab,
            parent_menu: menu.name,
            menu_structure: menu.route.length
              ? menu.route.reduce((acc, submenu) => {
                  acc[submenu.name] = submenu.route.length
                    ? submenu.route.map((item) => ({
                        name: item.name,
                        path: item.path,
                      }))
                    : [{ name: submenu.name, path: submenu.path }];
                  return acc;
                }, {})
              : {},
          }));

          setroleMenuData(updatedData);
        } else {
          setError("Invalid data format from API");
        }
      } catch (error) {
        console.error("API Error:", error);
        setError("Failed to fetch roles.");
      }
    };

    fetchRoleMenuData();
  }, []);

  const handleRoleClick = async (role) => {
    try {
      const res = await PermissionServices.getPermissionById(role.id_profile);
      const response = await RoleServices.getRoleById(role.id_profile);
      if (response) {
        setSelectedRole(response);
      }
      if (Array.isArray(res?.menu_permissions)) {
        // Transform API response into the expected state structure
        const updatedPermissions = {};

        res.menu_permissions.forEach((perm) => {
          updatedPermissions[perm.id_tab] = {
            view: perm.view === 1,
            add: perm.add === 1,
            edit: perm.edit === 1,
            delete: perm.delete === 1,
            export: perm.export === 1,
            all: perm.all === 1,
          };
        });

        setPermissions(updatedPermissions);
      } else {
        setPermissions([]);
      }
    } catch (error) {
      console.error("API Error:", error);
      setError("Failed to fetch role details.");
    }
  };

  const handleSubmit = async () => {
    try {
      const rolePermissions = Object.keys(permissions).map((id_tab) => {
        const perm = permissions[id_tab] || {};
        return {
          id_tab,

          view: perm.view === true,
          add: perm.add === true,
          edit: perm.edit === true,
          delete: perm.delete === true,
          export: perm.export === true,
          all: perm.all === true,
        };
      });

      const payload = {
        id_profile: selectedRole.id_profile,
        menu_permissions: rolePermissions,
      };

      const permissionResponse = await PermissionServices.addPermission(
        payload
      );

      if (permissionResponse?.status) {
        notifySuccess("Permissions updated successfully!");
      } else {
        setError("Failed to update permissions.");
      }
    } catch (error) {
      console.error("Error updating permissions:", error);
      setError("Error updating permissions.");
    }
  };

  const handleCheckboxChange = (e) => {
    const { id, checked } = e.target;

    const permissionTypes = ["view", "add", "edit", "delete", "export", "all"];
    const permissionType = permissionTypes.find((type) => id.endsWith(type));

    if (!permissionType) {
      console.warn("Invalid permission type detected:", id);
      return;
    }

    const menuName = id.slice(0, -permissionType.length);

    setPermissions((prev) => {
      const updatedPermissions = {
        ...prev,
        [menuName]: {
          ...(prev[menuName] || {}),
          [permissionType]: checked,
        },
      };

      // If "all" is checked, update all permissions
      if (permissionType === "all") {
        updatedPermissions[menuName] = {
          view: checked,
          add: checked,
          edit: checked,
          delete: checked,
          export: checked,
          all: checked,
        };
      } else {
        // Check if all permissions are enabled, then update "all"
        updatedPermissions[menuName].all = Object.values(
          updatedPermissions[menuName]
        ).every(Boolean);
      }

      return updatedPermissions;
    });
  };
  const handleTopCheckboxChange = (permissionType, checked) => {
    setPermissions((prevPermissions) => {
      const updatedPermissions = { ...prevPermissions };

      roleMenuData.forEach((menu) => {
        const parentKey = menu.parent_menu.toLowerCase();

        // Ensure parent menu structure exists
        if (!updatedPermissions[parentKey]) {
          updatedPermissions[parentKey] = {
            view: false,
            add: false,
            edit: false,
            delete: false,
            export: false,
            all: false,
          };
        }

        if (permissionType === "all") {
          Object.keys(updatedPermissions[parentKey]).forEach((key) => {
            updatedPermissions[parentKey][key] = checked;
          });
        } else {
          updatedPermissions[parentKey][permissionType] = checked;
          updatedPermissions[parentKey].all = Object.values(
            updatedPermissions[parentKey]
          ).every(Boolean);
        }

        // Update submenus and their items
        Object.entries(menu.menu_structure).forEach(([submenu, items]) => {
          const submenuKey = submenu.toLowerCase();

          if (!updatedPermissions[submenuKey]) {
            updatedPermissions[submenuKey] = {
              view: false,
              add: false,
              edit: false,
              delete: false,
              export: false,
              all: false,
            };
          }

          if (permissionType === "all") {
            Object.keys(updatedPermissions[submenuKey]).forEach((key) => {
              updatedPermissions[submenuKey][key] = checked;
            });
          } else {
            updatedPermissions[submenuKey][permissionType] = checked;
            updatedPermissions[submenuKey].all = Object.values(
              updatedPermissions[submenuKey]
            ).every(Boolean);
          }

          // Update individual items inside submenu
          items.forEach((item) => {
            const menuKey = item.name.toLowerCase();

            // Only add if the item name contains a space
            if (/\s/.test(item.name)) {
              if (!updatedPermissions[menuKey]) {
                updatedPermissions[menuKey] = {
                  view: false,
                  add: false,
                  edit: false,
                  delete: false,
                  export: false,
                  all: false,
                };
              }

              if (permissionType === "all") {
                Object.keys(updatedPermissions[menuKey]).forEach((key) => {
                  updatedPermissions[menuKey][key] = checked;
                });
              } else {
                updatedPermissions[menuKey][permissionType] = checked;
                updatedPermissions[menuKey].all = Object.values(
                  updatedPermissions[menuKey]
                ).every(Boolean);
              }
            }
          });
        });
      });

      return updatedPermissions;
    });
  };

  return (
    <>
      <PageTitle sidebar={sidebar().sidebar} />

      <div className="flex">
        <div className="w-1/4 pr-4">
          <div className="pb-2 dark:text-white">Roles</div>
          <Card
            className="min-w-0 overflow-hidden"
            style={{ backgroundColor: "transparent" }}
          >
            <ul className="space-y-1 m-0">
              {roles.map((role) => (
                <li
                  key={role.id_profile}
                  className={`block rounded-lg dark:bg-gray-600 dark:text-white px-4 py-2 text-sm font-medium ${
                    selectedRole?.id_profile === role.id_profile
                      ? "bg-green-500 text-white"
                      : "text-gray-700 hover:bg-gray-200"
                  }`}
                  onClick={() => handleRoleClick(role)}
                  style={{ cursor: "pointer" }}
                >
                  {role.name}
                </li>
              ))}
            </ul>
          </Card>
        </div>
        <div className="w-3/4">
          <div className="pb-2 flex justify-between items-center">
            <div className="dark:text-white">
              {selectedRole
                ? `${selectedRole.name} Access Menu`
                : "Access Menu"}
            </div>
            <div>
              <button
                className="permissionCancleBtn"
                onClick={() => {
                  setPermissions({});
                }}
              >
                Undo
              </button>
              <button
                className="permissionSubmitBtn"
                onClick={() => handleSubmit()}
              >
                Submit
              </button>
            </div>
          </div>

          {selectedRole && (
            <form className="shadow-xs bg-white dark:bg-gray-800 rounded-xl flex flex-col overflow-hidden">
              <div className="flex gap-8 pt-3 pr-6 pl-3 pb-1 dark:text-gray-100 dark:bg-gray-400 bg-gray-300">
                <div className="accessMenu font-bold dark:text-gray-100">
                  Menu
                </div>
                <div className="flex justify-between flex-1">
                  {["View", "Add", "Edit", "Delete", "Export", "All"].map(
                    (permission) => {
                      const isChecked = roleMenuData.every((menu) =>
                        Object.values(menu.menu_structure)
                          .flat()
                          .every(
                            (item) =>
                              permissions[item.name.toLowerCase()]?.[
                                permission.toLowerCase()
                              ]
                          )
                      );

                      return (
                        <div className="permissionCheckBox" key={permission}>
                          <input
                            type="checkbox"
                            id={`title${permission.toLowerCase()}`}
                            checked={isChecked} // Check if all permissions of this type are enabled
                            onChange={(e) =>
                              handleTopCheckboxChange(
                                permission.toLowerCase(),
                                e.target.checked
                              )
                            }
                          />
                          <label
                            htmlFor={`title${permission.toLowerCase()}`}
                            className="dark:text-gray-100"
                          >
                            {permission}
                          </label>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>

              {roleMenuData.map((menu, index) => (
                <div key={index} className="px-3 py-1">
                  {/* Parent Menu with "View" Checkbox */}
                  <div className="flex gap-8 py-2 bg-gray-200 dark:bg-gray-700">
                    <div className="accessMenu dark:text-gray-100">
                      {menu.parent_menu}
                    </div>

                    <div className="flex justify-between flex-1">
                      {["View"].map((permission) => {
                        const id = `${menu.parent_menu.toLowerCase()}${permission.toLowerCase()}`;
                        return (
                          <div className="permissionCheckBox" key={id}>
                            <input
                              type="checkbox"
                              id={id}
                              checked={
                                permissions[menu.parent_menu.toLowerCase()]?.[
                                  permission.toLowerCase()
                                ] || false
                              }
                              onChange={handleCheckboxChange}
                            />
                            <label htmlFor={id} className="dark:text-gray-100">
                              {permission}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Submenus */}
                  {Object.entries(menu.menu_structure)
                    .filter(([submenu, items]) => items.length > 0)
                    .map(([submenu, items], idx) => (
                      <React.Fragment key={idx}>
                        {/* Submenu (Parent) Checkbox */}
                        {[
                          "Hour Rental",
                          "Day Rental",
                          "Airport Pickup",
                          "Fixed Itinerary",
                        ].includes(submenu) && (
                          <div className="flex gap-8 py-2 bg-gray-200 dark:bg-gray-700">
                            <div className="accessMenu dark:text-gray-100">
                              {submenu}
                            </div>

                            <div className="flex justify-between flex-1">
                              {["View"].map((permission) => {
                                const id =
                                  submenu.replace(/\s|-/g, "").toLowerCase() +
                                  permission.toLowerCase();

                                return (
                                  <div className="permissionCheckBox" key={id}>
                                    <input
                                      type="checkbox"
                                      id={id}
                                      checked={
                                        permissions?.[
                                          submenu
                                            .replace(/\s|-/g, "")
                                            .toLowerCase()
                                        ]?.[permission.toLowerCase()] || false
                                      }
                                      onChange={handleCheckboxChange}
                                    />
                                    <label htmlFor={id}>{permission}</label>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Child Items Under Submenu */}
                        {items.map((item, subIdx) => (
                          <div className="flex gap-8 py-2" key={subIdx}>
                            {/* Sub-item name */}
                            <div className="accessMenu dark:text-gray-100">
                              {item.name}
                            </div>

                            {/* Sub-item Checkboxes */}
                            <div className="flex justify-between flex-1">
                              {[
                                "View",
                                "Add",
                                "Edit",
                                "Delete",
                                "Export",
                                "All",
                              ].map((permission) => {
                                const id = `${item.name.toLowerCase()}${permission.toLowerCase()}`;
                                return (
                                  <div className="permissionCheckBox" key={id}>
                                    <input
                                      type="checkbox"
                                      id={id}
                                      checked={
                                        permissions[item.name.toLowerCase()]?.[
                                          permission.toLowerCase()
                                        ] || false
                                      }
                                      onChange={handleCheckboxChange}
                                    />
                                    <label
                                      htmlFor={id}
                                      className="dark:text-gray-100"
                                    >
                                      {permission}
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </React.Fragment>
                    ))}
                </div>
              ))}
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default Permission;
